import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Grid, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Alert,
  List, ListItem, ListItemText, ListItemSecondaryAction, Tooltip,
  useTheme, useMediaQuery
} from '@mui/material';
import { Add, Edit, Delete, Circle } from '@mui/icons-material';
import { categoriesAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SlideTransition from '../../components/common/SlideTransition';
import DevBadge from '../../components/common/DevBadge';

const PRESET_COLORS = ['#00C853', '#2979FF', '#FF6D00', '#AA00FF', '#D50000', '#9E9E9E', '#00BCD4', '#FF4081', '#111111'];

export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#111111' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState({ open: false, catId: null });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const load = async () => {
    try {
      const res = await categoriesAPI.getAll();
      setCategories(res.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async () => {
    try {
      await categoriesAPI.seed();
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const openCreate = () => {
    setEditCat(null);
    setForm({ name: '', color: '#2979FF' });
    setError('');
    setOpenDialog(true);
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setForm({ name: cat.name, color: cat.color || '#111111' });
    setError('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      if (editCat) {
        await categoriesAPI.update(editCat._id, form);
      } else {
        await categoriesAPI.create(form);
      }
      setOpenDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catId) => {
    try {
      await categoriesAPI.delete(catId);
      setConfirm({ open: false, catId: null });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  if (loading) return <LoadingSpinner />;

  const defaults = categories.filter(c => c.isDefault);
  const custom = categories.filter(c => !c.isDefault);

  return (
    <Box>
      <DevBadge id="page:admin-categorias" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Categorías de Actividad</Typography>
          <Typography variant="body2" color="text.secondary">Gestiona las etiquetas para clasificar actividades</Typography>
        </Box>
        <Box display="flex" gap={1}>
          {defaults.length === 0 && (
            <Button variant="outlined" onClick={handleSeed}>Inicializar Categorías Base</Button>
          )}
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Nueva Categoría</Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Default Categories */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle1" fontWeight={600}>Categorías Base</Typography>
              <Typography variant="caption" color="text.secondary">Disponibles en todos los proyectos</Typography>
            </Box>
            <List>
              {defaults.map(cat => (
                <ListItem key={cat._id}>
                  <Circle sx={{ color: cat.color, fontSize: 14, mr: 1.5 }} />
                  <ListItemText primary={cat.name} />
                  <Chip label="Base" size="small" variant="outlined" />
                  <Tooltip title="Editar color">
                    <IconButton size="small" sx={{ ml: 1 }} onClick={() => openEdit(cat)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                </ListItem>
              ))}
              {defaults.length === 0 && (
                <ListItem>
                  <ListItemText secondary="No hay categorías base. Haz clic en 'Inicializar Categorías Base'." />
                </ListItem>
              )}
            </List>
          </Card>
        </Grid>

        {/* Custom Categories */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle1" fontWeight={600}>Categorías Personalizadas</Typography>
              <Typography variant="caption" color="text.secondary">Categorías adicionales creadas por administrador</Typography>
            </Box>
            <List>
              {custom.map(cat => (
                <ListItem key={cat._id}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: '2px' }}>
                      <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openEdit(cat)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }} onClick={() => setConfirm({ open: true, catId: cat._id })}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <Circle sx={{ color: cat.color || '#1B2B4B', fontSize: 14, mr: 1.5 }} />
                  <ListItemText primary={cat.name}
                    secondary={cat.isActive ? 'Activa' : 'Inactiva'} />
                </ListItem>
              ))}
              {custom.length === 0 && (
                <ListItem><ListItemText secondary="No hay categorías personalizadas." /></ListItem>
              )}
            </List>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {editCat ? 'Editar Categoría' : 'Nueva Categoría'}
          <DevBadge id="modal:categoria" inline />
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Nombre *" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <Box>
              <Typography variant="caption" color="text.secondary" mb={1} display="block">Color</Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                {PRESET_COLORS.map(c => (
                  <Box key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                    sx={{
                      width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                      border: form.color === c ? '3px solid #333' : '2px solid transparent',
                      transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.15)' }
                    }} />
                ))}
              </Box>
              <TextField type="color" value={form.color} size="small"
                onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                sx={{ width: 80 }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open} title="Eliminar Categoría"
        message="¿Desactivar esta categoría? Las actividades existentes conservarán la categoría."
        confirmLabel="Eliminar"
        onConfirm={() => handleDelete(confirm.catId)}
        onCancel={() => setConfirm({ open: false, catId: null })}
      />
    </Box>
  );
}
